import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Save, X, Trash2, Edit2, Clock } from "lucide-react";
import { API, authHeaders } from "./shared";

const MIN_RATE = 50;
const CATEGORIES = [
  { value: "carico", label: "Carico" },
  { value: "scarico", label: "Scarico" },
  { value: "inventario", label: "Inventario" },
  { value: "preparazione_spedizione", label: "Preparazione Spedizione" },
  { value: "altro", label: "Altro" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  date: todayISO(),
  client_email: "",
  client_name: "",
  hours: 1,
  hourly_rate: MIN_RATE,
  operator: "",
  category: "preparazione_spedizione",
  description: "",
});

export default function HoursTab({ clients = [] }) {
  const [month, setMonth] = useState(currentMonth());
  const [filterClient, setFilterClient] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (filterClient) params.set("client_email", filterClient);
      const r = await axios.get(`${API}/admin/warehouse/hours?${params}`, authHeaders());
      setData(r.data);
    } catch {
      toast.error("Errore nel caricamento delle ore");
    } finally {
      setLoading(false);
    }
  }, [month, filterClient]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      date: row.date,
      client_email: row.client_email,
      client_name: row.client_name || "",
      hours: row.hours,
      hourly_rate: row.hourly_rate,
      operator: row.operator || "",
      category: row.category || "altro",
      description: row.description || "",
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.client_email) { toast.error("Seleziona un cliente"); return; }
    if (!form.hours || form.hours <= 0) { toast.error("Inserisci le ore lavorate"); return; }
    if (!form.hourly_rate || form.hourly_rate < MIN_RATE) {
      toast.error(`La tariffa minima è ${MIN_RATE} €/h`);
      return;
    }
    // Autofill client name from clients list
    const cliMatch = clients.find((c) => c.email === form.client_email);
    const payload = { ...form, client_name: form.client_name || cliMatch?.name || cliMatch?.company || "" };
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/warehouse/hours/${editingId}`, payload, authHeaders());
        toast.success("Riga aggiornata");
      } else {
        await axios.post(`${API}/admin/warehouse/hours`, payload, authHeaders());
        toast.success("Ore registrate");
      }
      setShowForm(false);
      setForm(emptyForm());
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Eliminare questa sessione (${row.date} · ${row.hours}h)?`)) return;
    try {
      await axios.delete(`${API}/admin/warehouse/hours/${row.id}`, authHeaders());
      toast.success("Riga eliminata");
      await load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const rows = data?.rows || [];
  // Group per client for summary
  const clientBuckets = rows.reduce((acc, r) => {
    const key = r.client_email;
    if (!acc[key]) acc[key] = { email: key, name: r.client_name || key, hours: 0, amount: 0, rows: 0 };
    acc[key].hours += Number(r.hours || 0);
    acc[key].amount += Number(r.amount || (r.hours * r.hourly_rate) || 0);
    acc[key].rows += 1;
    return acc;
  }, {});
  const clientTotals = Object.values(clientBuckets).sort((a, b) => b.amount - a.amount);

  return (
    <div className="space-y-4" data-testid="hours-tab">
      <div className="bg-white border border-zinc-200 rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-300 rounded-sm px-3 py-1.5">
          <Clock size={14} className="text-zinc-500" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="text-sm outline-none bg-transparent"
            data-testid="hours-month"
          />
        </div>

        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="h-8 px-2 border border-zinc-300 rounded-sm text-sm bg-white"
          data-testid="hours-client-filter"
        >
          <option value="">Tutti i clienti</option>
          {clients.map((c) => (
            <option key={c.email} value={c.email}>{c.name || c.company || c.email}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-4 text-xs">
          <div>
            <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Ore Totali</div>
            <div className="font-bold text-[#09090B]" data-testid="hours-total-hours">{(data?.total_hours || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Importo</div>
            <div className="font-bold text-[#FF5200]" data-testid="hours-total-amount">{(data?.total_amount || 0).toFixed(2)} €</div>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-1 bg-[#09090B] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-black"
            data-testid="hours-new-btn"
          >
            <Plus size={12} /> Nuova sessione
          </button>
        </div>
      </div>

      {/* Client aggregation */}
      {clientTotals.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-2 border-b border-zinc-100 text-[10px] uppercase font-bold tracking-widest text-zinc-500">
            Riepilogo per Cliente
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Cliente</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Sessioni</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Ore</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Importo</th>
              </tr>
            </thead>
            <tbody>
              {clientTotals.map((cli) => (
                <tr key={cli.email} className="border-t border-zinc-100" data-testid={`hours-summary-${cli.email}`}>
                  <td className="px-4 py-2">
                    <div className="font-bold text-[#09090B]">{cli.name}</div>
                    <div className="text-[10px] text-zinc-400">{cli.email}</div>
                  </td>
                  <td className="px-3 py-2 text-right">{cli.rows}</td>
                  <td className="px-3 py-2 text-right">{cli.hours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-bold text-[#FF5200]">{cli.amount.toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sessions */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="hours-empty">
            Nessuna ora registrata per il periodo selezionato.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Data</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Cliente</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Categoria</th>
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Operatore / Note</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Ore</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Tariffa</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Importo</th>
                <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`hours-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2">
                    <div className="font-bold text-[#09090B]">{r.client_name || r.client_email}</div>
                    <div className="text-[10px] text-zinc-400">{r.client_email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-bold tracking-wide">
                      {CATEGORY_LABEL[r.category] || r.category}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.operator && <div className="font-medium text-[#09090B]">{r.operator}</div>}
                    {r.description && <div className="text-[10px] text-zinc-500">{r.description}</div>}
                    {!r.operator && !r.description && <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{Number(r.hours).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{Number(r.hourly_rate).toFixed(2)} €</td>
                  <td className="px-3 py-2 text-right font-bold text-[#FF5200]">
                    {Number(r.amount || r.hours * r.hourly_rate).toFixed(2)} €
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button
                      onClick={() => openEdit(r)}
                      className="inline-flex items-center border border-zinc-300 bg-white px-2 py-1 rounded-sm hover:bg-zinc-100 mr-1"
                      data-testid={`hours-edit-${r.id}`}
                      title="Modifica"
                    >
                      <Edit2 size={11} />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="inline-flex items-center border border-red-200 bg-white text-red-700 px-2 py-1 rounded-sm hover:bg-red-50"
                      data-testid={`hours-delete-${r.id}`}
                      title="Elimina"
                    >
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="hours-form-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#09090B]">
                {editingId ? "Modifica Sessione" : "Nuova Sessione Ore"}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="hours-form-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Data *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
                    data-testid="hours-form-date"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Cliente *</label>
                  <select
                    value={form.client_email}
                    onChange={(e) => {
                      const cli = clients.find((c) => c.email === e.target.value);
                      setForm({ ...form, client_email: e.target.value, client_name: cli?.name || cli?.company || "" });
                    }}
                    className="w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none bg-white"
                    data-testid="hours-form-client"
                  >
                    <option value="">— Seleziona cliente —</option>
                    {clients.map((c) => (
                      <option key={c.email} value={c.email}>{c.name || c.company || c.email}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Categoria *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none bg-white"
                    data-testid="hours-form-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Operatore</label>
                  <input
                    type="text"
                    value={form.operator}
                    onChange={(e) => setForm({ ...form, operator: e.target.value })}
                    placeholder="Nome dell'operatore"
                    className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
                    data-testid="hours-form-operator"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Ore *</label>
                  <input
                    type="number" step="0.25" min="0.25"
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })}
                    className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
                    data-testid="hours-form-hours"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">
                    Tariffa €/h * <span className="text-zinc-400 normal-case font-normal">(min {MIN_RATE}€)</span>
                  </label>
                  <input
                    type="number" step="0.50" min={MIN_RATE}
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })}
                    className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
                    data-testid="hours-form-rate"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Descrizione</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Dettagli del lavoro svolto (facoltativo)"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
                  data-testid="hours-form-description"
                />
              </div>

              {form.hours > 0 && form.hourly_rate >= MIN_RATE && (
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 uppercase font-bold tracking-widest">Importo Sessione</span>
                  <span className="text-lg font-bold text-[#FF5200]">
                    {(form.hours * form.hourly_rate).toFixed(2)} €
                  </span>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
              <button
                onClick={() => setShowForm(false)}
                className="border border-zinc-300 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-100"
                data-testid="hours-form-cancel"
              >
                Annulla
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-blue-800 disabled:opacity-50"
                data-testid="hours-form-submit"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {editingId ? "Aggiorna" : "Registra Ore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
